import { useState, useEffect } from 'react';
import { api, ApiError } from '../api/client';
import type { RiskLevel } from '../api/types';

// =============================================================================
// Slice 7.9 (item 3) — Entity Creation Drawer
//
// Allows the user to create a brand-new threat event / vulnerability /
// predisposing condition / impact entity from inside the Composition Editor,
// without leaving the composition workflow.
//
// "Block-not-replace" pattern: the drawer slides in over the modal, dimming
// the modal's content. The modal stays mounted in the background so its
// state — picked asset_type, partially-filled fields, etc. — survives.
// On Save, the drawer:
//   1. POSTs to the appropriate /admin/threat-catalog/{events|vulns|...}
//   2. Returns the new entity ID via the onCreated callback
//   3. Closes
// The parent (CompositionEditor) refetches its filterQ to pull in the new
// entity, then auto-selects it in the relevant dropdown.
//
// Each entity type has a slightly different shape — events have type +
// event_relevance + timeline, vulnerabilities have severity (a Likert
// level), predisposing conditions have pervasiveness, impacts have just
// type. The drawer is a discriminated union over `entityKind`.
// =============================================================================

export type EntityKind = 'event' | 'vulnerability' | 'predisposing_condition' | 'impact';

interface DrawerProps {
  open: boolean;
  kind: EntityKind;
  onClose: () => void;
  onCreated: (newId: string, newName: string) => void;
}

const ENDPOINTS: Record<EntityKind, string> = {
  event: '/admin/threat-catalog/events',
  vulnerability: '/admin/threat-catalog/vulnerabilities',
  predisposing_condition: '/admin/threat-catalog/predisposing-conditions',
  impact: '/admin/threat-catalog/impacts',
};

const TITLES: Record<EntityKind, string> = {
  event: 'New threat event',
  vulnerability: 'New vulnerability',
  predisposing_condition: 'New predisposing condition',
  impact: 'New impact',
};

// Likert levels accepted as severity / pervasiveness on the backend.
const LIKERT_OPTIONS: Array<{ value: RiskLevel; label: string }> = [
  { value: 'Very_low', label: 'Very Low' },
  { value: 'Low', label: 'Low' },
  { value: 'Moderate', label: 'Moderate' },
  { value: 'High', label: 'High' },
  { value: 'Very_high', label: 'Very High' },
  { value: 'Critical', label: 'Critical' },
];

interface EventForm {
  legacy_code: string;
  name: string;
  type: string;
  sub_type: string;
  description: string;
  event_relevance: string;
  timeline: string;
  mitigating_factors: string;
}

interface VulnForm {
  legacy_code: string;
  name: string;
  type: string;
  description: string;
  severity: RiskLevel;
}

interface PredisposForm {
  legacy_code: string;
  name: string;
  type: string;
  description: string;
  pervasiveness: RiskLevel;
}

interface ImpactForm {
  legacy_code: string;
  name: string;
  type: string;
  description: string;
}

const EMPTY_EVENT: EventForm = {
  legacy_code: '', name: '', type: '', sub_type: '',
  description: '', event_relevance: 'Moderate', timeline: '', mitigating_factors: '',
};
const EMPTY_VULN: VulnForm = {
  legacy_code: '', name: '', type: '', description: '', severity: 'Moderate',
};
const EMPTY_PREDISPOS: PredisposForm = {
  legacy_code: '', name: '', type: '', description: '', pervasiveness: 'Moderate',
};
const EMPTY_IMPACT: ImpactForm = {
  legacy_code: '', name: '', type: '', description: '',
};

export function EntityCreationDrawer({ open, kind, onClose, onCreated }: DrawerProps) {
  const [eventForm, setEventForm] = useState<EventForm>(EMPTY_EVENT);
  const [vulnForm, setVulnForm] = useState<VulnForm>(EMPTY_VULN);
  const [predisposForm, setPredisposForm] = useState<PredisposForm>(EMPTY_PREDISPOS);
  const [impactForm, setImpactForm] = useState<ImpactForm>(EMPTY_IMPACT);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset form when drawer opens
  useEffect(() => {
    if (!open) return;
    setEventForm(EMPTY_EVENT);
    setVulnForm(EMPTY_VULN);
    setPredisposForm(EMPTY_PREDISPOS);
    setImpactForm(EMPTY_IMPACT);
    setError(null);
  }, [open, kind]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  function buildPayload(): Record<string, unknown> | null {
    if (kind === 'event') {
      if (!eventForm.name.trim() || !eventForm.type.trim()) {
        setError('Name and Type are required');
        return null;
      }
      return {
        legacy_code: eventForm.legacy_code.trim() || null,
        name: eventForm.name.trim(),
        type: eventForm.type.trim(),
        sub_type: eventForm.sub_type.trim() || null,
        description: eventForm.description.trim() || null,
        event_relevance: eventForm.event_relevance,
        timeline: eventForm.timeline.trim() || null,
        mitigating_factors: eventForm.mitigating_factors.trim() || null,
      };
    }
    if (kind === 'vulnerability') {
      if (!vulnForm.name.trim() || !vulnForm.type.trim()) {
        setError('Name and Type are required');
        return null;
      }
      return {
        legacy_code: vulnForm.legacy_code.trim() || null,
        name: vulnForm.name.trim(),
        type: vulnForm.type.trim(),
        description: vulnForm.description.trim() || null,
        severity: vulnForm.severity,
      };
    }
    if (kind === 'predisposing_condition') {
      if (!predisposForm.name.trim() || !predisposForm.type.trim()) {
        setError('Name and Type are required');
        return null;
      }
      return {
        legacy_code: predisposForm.legacy_code.trim() || null,
        name: predisposForm.name.trim(),
        type: predisposForm.type.trim(),
        description: predisposForm.description.trim() || null,
        pervasiveness: predisposForm.pervasiveness,
      };
    }
    if (!impactForm.name.trim() || !impactForm.type.trim()) {
      setError('Name and Type are required');
      return null;
    }
    return {
      legacy_code: impactForm.legacy_code.trim() || null,
      name: impactForm.name.trim(),
      type: impactForm.type.trim(),
      description: impactForm.description.trim() || null,
    };
  }

  async function handleSave() {
    setError(null);
    const payload = buildPayload();
    if (!payload) return;
    setSubmitting(true);
    try {
      const res = await api.post<{ id: string }>(ENDPOINTS[kind], payload);
      const newId = (res as { id: string }).id;
      onCreated(newId, (payload.name as string));
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    // Slice 7.9: drawer is rendered as a fixed overlay above the rest of
    // the page. The semi-transparent backdrop dims the modal behind so
    // the user knows the drawer is active. Click-outside dismisses; the
    // drawer slides from the right edge.
    <div className="fixed inset-0 z-[60]" role="dialog" aria-label={TITLES[kind]}>
      <div
        className="absolute inset-0 bg-black/30"
        onClick={() => !submitting && onClose()}
      />
      <div className="absolute right-0 top-0 bottom-0 w-[480px] max-w-[95vw] bg-white shadow-2xl border-l border-ink-200 flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-ink-200">
          <h2 className="text-base font-semibold text-ink-900">{TITLES[kind]}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-ink-400 hover:text-ink-700 disabled:opacity-50"
            aria-label="Close drawer"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-auto px-4 py-3 space-y-3 text-sm">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-sm text-xs">
              {error}
            </div>
          )}
          {kind === 'event' && (
            <EventFields form={eventForm} onChange={setEventForm} />
          )}
          {kind === 'vulnerability' && (
            <VulnFields form={vulnForm} onChange={setVulnForm} />
          )}
          {kind === 'predisposing_condition' && (
            <PredisposFields form={predisposForm} onChange={setPredisposForm} />
          )}
          {kind === 'impact' && (
            <ImpactFields form={impactForm} onChange={setImpactForm} />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-ink-200 bg-ink-50">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm px-3 py-1.5 border border-ink-200 rounded-sm bg-white hover:bg-ink-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting}
            className="text-sm px-3 py-1.5 rounded-sm bg-accent text-white hover:bg-accent/90 disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save & select'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Per-entity field forms ----------

function FieldRow({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <label className="block text-xs font-medium text-ink-700 mb-1">
        {label}
        {required && <span className="text-accent ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, maxLength }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full border border-ink-200 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-accent"
    />
  );
}

function TextArea({ value, onChange, placeholder, rows }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows ?? 3}
      className="w-full border border-ink-200 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-accent"
    />
  );
}

function LikertSelect({ value, onChange }: { value: RiskLevel; onChange: (v: RiskLevel) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as RiskLevel)}
      className="w-full border border-ink-200 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-accent"
    >
      {LIKERT_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function EventFields({ form, onChange }: { form: EventForm; onChange: (f: EventForm) => void }) {
  const patch = <K extends keyof EventForm>(k: K, v: EventForm[K]) => onChange({ ...form, [k]: v });
  return (
    <>
      <FieldRow label="Name" required>
        <TextInput value={form.name} onChange={(v) => patch('name', v)} placeholder="e.g. Privilege escalation" maxLength={255} />
      </FieldRow>
      <FieldRow label="Type" required>
        <TextInput value={form.type} onChange={(v) => patch('type', v)} placeholder="e.g. Adversarial, Non-adversarial" maxLength={128} />
      </FieldRow>
      <FieldRow label="Sub-type">
        <TextInput value={form.sub_type} onChange={(v) => patch('sub_type', v)} placeholder="(optional)" maxLength={128} />
      </FieldRow>
      <FieldRow label="Event relevance" required>
        <TextInput value={form.event_relevance} onChange={(v) => patch('event_relevance', v)} placeholder="e.g. Moderate, High" maxLength={64} />
      </FieldRow>
      <FieldRow label="Timeline">
        <TextInput value={form.timeline} onChange={(v) => patch('timeline', v)} placeholder="(optional)" maxLength={255} />
      </FieldRow>
      <FieldRow label="Legacy code">
        <TextInput value={form.legacy_code} onChange={(v) => patch('legacy_code', v)} placeholder="(optional)" maxLength={64} />
      </FieldRow>
      <FieldRow label="Description">
        <TextArea value={form.description} onChange={(v) => patch('description', v)} placeholder="(optional)" rows={2} />
      </FieldRow>
      <FieldRow label="Mitigating factors">
        <TextArea value={form.mitigating_factors} onChange={(v) => patch('mitigating_factors', v)} placeholder="(optional)" rows={2} />
      </FieldRow>
    </>
  );
}

function VulnFields({ form, onChange }: { form: VulnForm; onChange: (f: VulnForm) => void }) {
  const patch = <K extends keyof VulnForm>(k: K, v: VulnForm[K]) => onChange({ ...form, [k]: v });
  return (
    <>
      <FieldRow label="Name" required>
        <TextInput value={form.name} onChange={(v) => patch('name', v)} placeholder="e.g. SQL injection" maxLength={255} />
      </FieldRow>
      <FieldRow label="Type" required>
        <TextInput value={form.type} onChange={(v) => patch('type', v)} placeholder="e.g. Software, Configuration" maxLength={128} />
      </FieldRow>
      <FieldRow label="Severity" required>
        <LikertSelect value={form.severity} onChange={(v) => patch('severity', v)} />
      </FieldRow>
      <FieldRow label="Legacy code">
        <TextInput value={form.legacy_code} onChange={(v) => patch('legacy_code', v)} placeholder="(optional)" maxLength={64} />
      </FieldRow>
      <FieldRow label="Description">
        <TextArea value={form.description} onChange={(v) => patch('description', v)} placeholder="(optional)" rows={3} />
      </FieldRow>
    </>
  );
}

function PredisposFields({ form, onChange }: { form: PredisposForm; onChange: (f: PredisposForm) => void }) {
  const patch = <K extends keyof PredisposForm>(k: K, v: PredisposForm[K]) => onChange({ ...form, [k]: v });
  return (
    <>
      <FieldRow label="Name" required>
        <TextInput value={form.name} onChange={(v) => patch('name', v)} placeholder="e.g. Weak access controls" maxLength={255} />
      </FieldRow>
      <FieldRow label="Type" required>
        <TextInput value={form.type} onChange={(v) => patch('type', v)} placeholder="e.g. Information, Architectural" maxLength={128} />
      </FieldRow>
      <FieldRow label="Pervasiveness" required>
        <LikertSelect value={form.pervasiveness} onChange={(v) => patch('pervasiveness', v)} />
      </FieldRow>
      <FieldRow label="Legacy code">
        <TextInput value={form.legacy_code} onChange={(v) => patch('legacy_code', v)} placeholder="(optional)" maxLength={64} />
      </FieldRow>
      <FieldRow label="Description">
        <TextArea value={form.description} onChange={(v) => patch('description', v)} placeholder="(optional)" rows={3} />
      </FieldRow>
    </>
  );
}

function ImpactFields({ form, onChange }: { form: ImpactForm; onChange: (f: ImpactForm) => void }) {
  const patch = <K extends keyof ImpactForm>(k: K, v: ImpactForm[K]) => onChange({ ...form, [k]: v });
  return (
    <>
      <FieldRow label="Name" required>
        <TextInput value={form.name} onChange={(v) => patch('name', v)} placeholder="e.g. Data exfiltration" maxLength={255} />
      </FieldRow>
      <FieldRow label="Type" required>
        <TextInput value={form.type} onChange={(v) => patch('type', v)} placeholder="e.g. Confidentiality, Integrity, Availability" maxLength={128} />
      </FieldRow>
      <FieldRow label="Legacy code">
        <TextInput value={form.legacy_code} onChange={(v) => patch('legacy_code', v)} placeholder="(optional)" maxLength={64} />
      </FieldRow>
      <FieldRow label="Description">
        <TextArea value={form.description} onChange={(v) => patch('description', v)} placeholder="(optional)" rows={3} />
      </FieldRow>
    </>
  );
}
